import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useSession, useUser } from '@clerk/clerk-react';
import { LoginProfile, IdentityPlatform } from '@playcastdotio/CoreTypes';
import { authStore, loginProfileState } from '../../api/stores/globalStores';
import { connect, disconnect } from '../../api/modules/realtimeMessaging';
import { queryProfile } from '../../api/modules/realtimeMessaging/handlers/profileMessage';
import { ownerAliasState, playerNameState, profileState } from '../../api/stores/lobbyStores';
import { determineProfileSlug, determinePlatformFromAliases } from '@playcastdotio/SharedComponents';
import { saveDisplayName } from '../../api/modules/player';
import { notifyMobileInstallReminder } from '../../api/modules/notifications';

interface AuthWrapperProps {
  children: React.ReactNode;
}

// Key for tracking first login
const FIRST_LOGIN_FLAG = 'playcast_first_login';

export const AuthWrapper = ({ children }: AuthWrapperProps) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { session } = useSession();
  const { user } = useUser();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false);
  const isConnected = useRef(false);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialCheckDoneRef = useRef(false);

  // Set first login flag after 3 seconds if not logged in
  useEffect(() => {
    if (isLoaded && !initialCheckDoneRef.current) {
      const timer = setTimeout(() => {
        initialCheckDoneRef.current = true;

        // If user is not logged in, set the first login flag
        if (!isSignedIn) {
          console.log('[AuthWrapper] User not logged in after initial delay, setting first login flag');
          localStorage.setItem(FIRST_LOGIN_FLAG, 'true');
        } else {
          console.log('[AuthWrapper] User already logged in after initial delay, no first login flag needed');
        }
      }, 3000); // 3 second delay

      return () => clearTimeout(timer);
    }
  }, [isLoaded, isSignedIn]);

  // Establish WebSocket connection when user is signed in
  useEffect(() => {
    if (isSignedIn && !isConnected.current) {
      // Clear any existing timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      // Establish connection
      connectionTimeoutRef.current = setTimeout(() => {
        console.log('[AuthWrapper] Establishing WebSocket connection');
        connect();
        isConnected.current = true;
      }, 100);
    } else if (!isSignedIn && isConnected.current) {
      console.log('[AuthWrapper] Disconnecting WebSocket connection');
      disconnect();
      isConnected.current = false;
    }

    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      if (isConnected.current) {
        console.log('[AuthWrapper] Cleaning up WebSocket connection');
        disconnect();
        isConnected.current = false;
      }
    };
  }, [isSignedIn]);

  // Detect actual sign-in event and check for first login flag
  useEffect(() => {
    // Skip if auth isn't loaded yet
    if (!isLoaded) return;

    // Detect sign-in event (transition from not signed in to signed in)
    if (isSignedIn) {
      console.log('[AuthWrapper] Login event detected');

      // Check for first login flag
      const hasFirstLoginFlag = localStorage.getItem(FIRST_LOGIN_FLAG) === 'true';

      // Send mobile install reminder notification if user is on mobile AND it's a new account
      notifyMobileInstallReminder(hasFirstLoginFlag, user?.primaryEmailAddress?.emailAddress);

      if (hasFirstLoginFlag) {
        console.log('[AuthWrapper] First login flag found, will redirect to profile');
        // Clear the flag immediately to prevent multiple redirects
        localStorage.removeItem(FIRST_LOGIN_FLAG);
        // Trigger profile check and redirect
        setTimeout(() => {
          setIsCheckingProfile(true);
        }, 1000);
      } else {
        console.log('[AuthWrapper] No first login flag found, no redirect needed');
      }
    }
  }, [isSignedIn, isLoaded]);

  // Function to update auth state with current token
  const updateAuthState = useCallback(async () => {
    if (isSignedIn && user && isLoaded && session) {
      try {
        // Check if we are close to expiring before refreshing
        const expTime = authStore.currentState.expiresAt;
        if (expTime && expTime - Date.now() > 5 * 60 * 1000) {
          console.log('[AuthWrapper] Token is not close to expiration');
          return;
        }

        // Reload session to ensure we have latest token
        // await session.reload();

        // Get JWT token
        const token = await session.getToken({ template: 'host-token' });
        if (!token) {
          console.warn('[AuthWrapper] No token received from session');
          return;
        }

        // Decode JWT to get expiration time
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const expiresAt = payload.exp * 1000; // Convert to milliseconds

          // Create profile object
          const profile: LoginProfile = {
            accessToken: token,
            name: user.username ?? '',
            email: user.primaryEmailAddress?.toString() ?? '',
            phoneNumber: user.primaryPhoneNumber?.toString() ?? '',
            picture: user.imageUrl ?? '',
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            hostAuthId: user.id ?? '',
            expiresAt: expiresAt,
          };

          console.log('[AuthWrapper] Token expires at:', new Date(expiresAt).toLocaleString());

          loginProfileState.updateState({
            ...loginProfileState.currentState,
            ...profile,
          });

          // Update name state
          if (user.username) {
            playerNameState.updateState({
              name: user.username,
            });
            saveDisplayName();
          }

          // Update auth state
          authStore.updateState({
            ...authStore.currentState,
            accessToken: token,
            expiresAt: expiresAt,
            playerAuthId: user.id ?? '',
          });
        }
      } catch (error) {
        console.error('[AuthWrapper] Error updating auth state:', error);
      }
    } else if (isLoaded && !isSignedIn) {
      // Reset auth state when not signed in
      authStore.updateState({
        ...authStore.currentState,
        accessToken: '',
        expiresAt: 0,
        playerAuthId: '',
      });
    }
  }, [isSignedIn, user, isLoaded, session]);

  // Utility function to determine profile URL with auth and user checks
  const getProfileUrl = useCallback(async () => {
    // Wait for auth to resolve and user object to be available
    if (!isLoaded || !isSignedIn || !user) {
      console.log('[AuthWrapper] Auth not ready or user not available');
      return null;
    }

    // Get owner aliases - use them if available but don't wait for them
    const ownerAliases = ownerAliasState.currentState.aliases;

    // Determine the platform based on available aliases
    const platform = determinePlatformFromAliases(ownerAliases) || IdentityPlatform.CLERK;

    // Use determineProfileSlug to get the profile URL
    const profileSlug = determineProfileSlug({
      authId: user.id,
      username: user.username ?? '',
      defaultAlias: ownerAliases.default,
      platform: platform,
    });

    if (!profileSlug) {
      console.error('[AuthWrapper] Could not determine profile slug');
      return null;
    }

    return `/${profileSlug}`;
  }, [isLoaded, isSignedIn, user]);

  // Check profile and redirect after authentication
  useEffect(() => {
    const checkProfileAndRedirect = async () => {
      if (isCheckingProfile && user?.id) {
        let ownerAliases = ownerAliasState.currentState.aliases;
        if (!ownerAliases.default) {
          ownerAliases = await new Promise((resolve) => {
            const listener = ownerAliasState.addListener((state) => {
              if (state.aliases.default) {
                ownerAliasState.removeListener(listener);
                resolve(state.aliases);
              }
            });
          });
        }

        console.log('[AuthWrapper] Setting up profile listener for redirect');

        // Set up a listener for profile data
        const listener = profileState.addListener((state) => {
          console.log('[AuthWrapper] Profile state update:', 'isLoading:', state.isLoading, 'error:', state.error ? 'yes' : 'no');

          // Once profile data is loaded, log it (redirect disabled)
          if (!state.isLoading && !state.error && state.profile?.displayName) {
            console.log('[AuthWrapper] Profile loaded successfully (redirect disabled)');
            setIsCheckingProfile(false);
            
            // Redirect to profile page logic removed - keeping user on current page
            // if (window.location.pathname === '/') {
            //   getProfileUrl().then((profileUrl) => {
            //     if (profileUrl) {
            //       window.location.href = profileUrl;
            //     }
            //   });
            // }
          }
        });

        if (window.location.pathname === '/') {
          // Query profile to ensure aliases are set up in the database
          console.log('[AuthWrapper] Querying profile for first-time auth, username:', ownerAliases.default);
          queryProfile(ownerAliases.default, IdentityPlatform.CLERK);
        }

        return () => {
          profileState.removeListener(listener);
        };
      }
    };

    checkProfileAndRedirect();
  }, [isCheckingProfile, user, getProfileUrl]);

  // Initial auth state update
  useEffect(() => {
    if (isLoaded) {
      updateAuthState().then(() => {
        // Set auth loading to false once initial auth state is updated
        authStore.updateState({
          ...authStore.currentState,
          loaded: true,
        });
      });
    }
  }, [isLoaded, isSignedIn, updateAuthState]);

  // Set up token refresh interval
  useEffect(() => {
    // Only set up interval if signed in and loaded
    if (isSignedIn && isLoaded) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Function to check token expiration
      const checkTokenExpiration = async () => {
        const currentTime = Date.now();
        const tokenExpiresAt = authStore.currentState.expiresAt;

        // If token is expired or about to expire in the next 1 minute
        if (tokenExpiresAt && currentTime > tokenExpiresAt - 1 * 60 * 1000) {
          console.log('[AuthWrapper] Token needs refresh');
          await updateAuthState();
        }
      };

      // Immediately check expiration
      checkTokenExpiration();

      // Set up interval
      intervalRef.current = setInterval(checkTokenExpiration, 60 * 1000); // Check every minute
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoaded, isSignedIn, updateAuthState]); // Removed tokenRefreshInterval from dependencies

  // Render children when auth is loaded
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
